import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import {
  isSameDay,
  requestDateFormat,
  toEndOfDay,
  toStartOfDay,
} from '@app/shared/utils/date-utils';
import { IColumn } from '@shared/interceptors/column.interface';
import { APPLICATION_TYPE_OPTIONS, NO_ACTIVITY_DAYS } from '@app/shared/constants/global.constant';
import { TableComponent } from '@shared/components/table-component/table.component';
import { DatePickerComponent } from '@shared/components/date-picker-component/date-picker-component';
import { KioskDetailComponent } from '@app/pages/dashboard/components/kiosk-detail/kiosk-detail.component';
import { OnlineCountersTableComponent } from './components/online-counters-table/online-counters-table.component';
import { ArchivedCountersTableComponent } from './components/archived-counters-table/archived-counters-table.component';

import { Card } from 'primeng/card';
import { UIChart } from 'primeng/chart';
import { Select } from 'primeng/select';
import { Tooltip } from 'primeng/tooltip';
import { FloatLabel } from 'primeng/floatlabel';
import { SelectButton } from 'primeng/selectbutton';
import { DatePickerModule } from 'primeng/datepicker';
import { UserModel } from '@shared/models/user.model';
import { KioskTableComponent } from './components/kiosk-table/kiosk-table.component';
import { PortsService } from '@app/shared/services/ports.service';
import { PortActivity } from '@app/shared/interfaces/ports-activity.interface';
import { DurationPipe } from '@app/shared/pipes/duration.pipe';

@Component({
  selector: 'app-dashboard',
  templateUrl: 'dashboard.component.html',
  styleUrl: 'dashboard.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableComponent,
    DatePickerComponent,
    OnlineCountersTableComponent,
    ArchivedCountersTableComponent,
    Card,
    Select,
    Tooltip,
    UIChart,
    FloatLabel,
    SelectButton,
    DatePickerModule,
    KioskTableComponent,
    DurationPipe,
  ],
})
export class DashboardComponent implements OnInit {
  statusColumnTemplate = viewChild('statusColumnTemplate');
  activeColumnTemplate = viewChild('activeColumnTemplate');

  portsService = inject(PortsService);

  applicationTypeOptions = APPLICATION_TYPE_OPTIONS;

  portsData = signal<string[]>([]);
  selectedPort = signal<string>('SAW');
  portSelectBoxLoading = signal<boolean>(false);
  selectedDateRange = signal<any>([new Date(), new Date()]);
  selectedStartTime = signal<string>('00:00');
  previousStartTime = signal<string>('00:00');
  selectedEndTime = signal<string>('23:59');
  previousEndTime = signal<string>('23:59');
  selectedApplicationType = signal<string>(this.applicationTypeOptions[0].value);

  // Chart Variables
  counterChartData = signal<any>(null);
  counterChartOptions = signal<any>(null);
  kioskChartData = signal<any>(null);
  kioskChartOptions = signal<any>(null);
  portsActivityData = signal<PortActivity | null>(null);

  // Session Detail Table Variables
  selectedCounterStatusType = signal<'online_counter' | 'archived_counter'>('online_counter');
  urlTableCols: IColumn[] = [];
  urlTableData = signal<PortActivity['serverPingInformations'] | null>(null);
  urlTableLoading = signal<boolean>(false);
  activeCountersTableCols: IColumn[] = [];
  activeCounterTablesData = signal<any>([]);
  activeCounterTablesLoading = signal<boolean>(false);
  authUser!: UserModel;
  timeFormatOptions: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };

  sessionStatusOptions: any[] = [
    { label: 'Online Counters', value: 'online_counter' },
    { label: 'Archived Counters', value: 'archived_counter' },
  ];

  getIsSameDay = computed(() => {
    if (this.selectedDateRange().length > 0) {
      return isSameDay(this.selectedDateRange()[0], this.selectedDateRange()[1]);
    } else {
      return false;
    }
  });

  isChartFill = computed(() => {
    return this.portsActivityData()?.activitySnapshots.find(
      (snapshot) => snapshot.counterCount > 0,
    );
  });

  ngOnInit() {
    this.initTablesCols();
    this.authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');

    this.getPorts();
    this.getPortsActivity();
    this.getPortsActivityCount();
  }

  // Initialize Operations
  initTablesCols() {
    this.urlTableCols = [
      { key: 'url', label: 'URL' },
      { key: 'serverStatus', label: 'Status', template: this.statusColumnTemplate() },
    ];

    this.activeCountersTableCols = [
      { key: 'port', label: 'Active', template: this.activeColumnTemplate() },
      { key: 'activeCounter', label: 'Active Counter' },
    ];
  }

  // Chart Operations
  setCounterChart() {
    const labels: string[] = [];
    const userLoggedInDataset: number[] = [];
    const counterDataset: number[] = [];

    this.portsActivityData()?.activitySnapshots.forEach((snapshot: any) => {
      if (this.getIsSameDay()) {
        const startTime = snapshot.label.split('_')[0];
        const endTime = snapshot.label.split('_')[1];
        labels.push(
          new Date(startTime).toLocaleTimeString([], this.timeFormatOptions) +
            ' - ' +
            new Date(endTime).toLocaleTimeString([], this.timeFormatOptions),
        );
      } else {
        labels.push(new Date(snapshot.label).toLocaleDateString());
      }
      userLoggedInDataset.push(snapshot.userCount);
      counterDataset.push(snapshot.counterCount);
    });

    this.counterChartData.set({
      labels: labels,
      datasets: [
        {
          label: 'Users Logged in',
          backgroundColor: '#FEB914',
          categoryPercentage: 0.25,
          barPercentage: 0.7,
          data: userLoggedInDataset,
        },
        {
          label: 'Counter',
          backgroundColor: '#0E8345',
          categoryPercentage: 0.25,
          barPercentage: 0.7,
          data: counterDataset,
        },
      ],
    });

    this.counterChartOptions.set({
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Count',
          },
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          labels: {
            color: '#0D0F11',
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 5,
            boxHeight: 5,
            font: { size: 12 },
          },
        },
        tooltip: {
          callbacks: {
            footer: this.chartTooltipFooter,
          },
        },
      },
    });
  }

  setKioskChart() {
    const labels: string[] = [];
    const userLoggedInDataset: number[] = [];
    const kioskDataset: number[] = [];

    this.portsActivityData()?.activitySnapshots.forEach((snapshot: any) => {
      if (this.getIsSameDay()) {
        const startTime = snapshot.label.split('_')[0];
        const endTime = snapshot.label.split('_')[1];

        labels.push(
          new Date(startTime).toLocaleTimeString([], this.timeFormatOptions) +
            ' - ' +
            new Date(endTime).toLocaleTimeString([], this.timeFormatOptions),
        );
      } else {
        labels.push(new Date(snapshot.label).toLocaleDateString());
      }
      userLoggedInDataset.push(snapshot.userCount);
      kioskDataset.push(snapshot.counterCount);
    });

    this.kioskChartData.set({
      labels: labels,
      datasets: [
        {
          label: 'Users Logged in',
          backgroundColor: '#FEB914',
          categoryPercentage: 0.25,
          barPercentage: 0.7,
          data: userLoggedInDataset,
        },
        {
          label: 'Kiosk',
          backgroundColor: '#0E8345',
          categoryPercentage: 0.25,
          barPercentage: 0.7,
          data: kioskDataset,
        },
      ],
    });

    this.kioskChartOptions.set({
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: 'Count',
          },
        },
      },
      plugins: {
        legend: {
          position: 'bottom',
          align: 'start',
          labels: {
            color: '#0D0F11',
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 5,
            boxHeight: 5,
            font: { size: 12 },
          },
          tooltip: {
            callbacks: {
              footer: this.chartTooltipFooter,
            },
          },
        },
      },
    });
  }

  chartTooltipFooter = (tooltipItems: any) => {
    const index = tooltipItems[0].dataIndex;
    const snapshot = this.portsActivityData()?.activitySnapshots[index];
    if (!snapshot) return '';

    const totalSeconds = Math.floor(snapshot.activityTime / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `Activity Time: ${hours}h ${minutes}m ${seconds}s`;
  };

  // API Call Operations
  getPorts() {
    this.portSelectBoxLoading.set(true);
    this.portsService.getPorts().subscribe({
      next: (response) => {
        this.portsData.set(['ALL', ...response]);
        this.portSelectBoxLoading.set(false);
      },
      error: (error: any) => {
        console.log(error);
        this.portSelectBoxLoading.set(false);
      },
    });
  }

  getPortsActivity() {
    let unit: string = '';
    let startDate: string = '';
    let endDate: string = '';
    const selectedPortFilter = this.selectedPort() === 'ALL' ? undefined : this.selectedPort();
    this.urlTableLoading.set(true);

    if (this.selectedDateRange().length > 0) {
      const [startDateRaw, endDateRaw] = this.selectedDateRange();
      const startTimeHours = this.getIsSameDay()
        ? Number(this.selectedStartTime().split(':')[0])
        : 0;
      const startTimeMinutes = this.getIsSameDay()
        ? Number(this.selectedStartTime().split(':')[1])
        : 0;
      const endTimeHours = this.getIsSameDay() ? Number(this.selectedEndTime().split(':')[0]) : 23;
      const endTimeMinutes = this.getIsSameDay()
        ? Number(this.selectedEndTime().split(':')[1])
        : 59;

      unit = this.getIsSameDay() ? 'hour' : 'day';

      startDate = new Date(
        Date.UTC(
          new Date(startDateRaw).getFullYear(),
          new Date(startDateRaw).getMonth(),
          new Date(startDateRaw).getDate(),
          startTimeHours,
          startTimeMinutes,
          0,
        ),
      ).toISOString();

      endDate = new Date(
        Date.UTC(
          new Date(endDateRaw).getFullYear(),
          new Date(endDateRaw).getMonth(),
          new Date(endDateRaw).getDate(),
          endTimeHours,
          endTimeMinutes,
          0,
        ),
      ).toISOString();
    }

    this.portsService
      .getPortsActivity(
        selectedPortFilter,
        unit,
        startDate,
        endDate,
        this.selectedApplicationType(),
      )
      .subscribe({
        next: (response) => {
          this.portsActivityData.set(response);
          this.urlTableData.set(response.serverPingInformations);
          this.setCounterChart();
          this.setKioskChart();
          this.urlTableLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.urlTableLoading.set(false);
        },
      });
  }

  getPortsActivityCount() {
    this.activeCounterTablesLoading.set(true);
    this.portsService.getPortsActivityCount().subscribe({
      next: (response) => {
        const entries = Object.entries(response).sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
        const orderedList = entries.map(([port, activeCounter]) => ({
          port,
          activeCounter: activeCounter.toString(),
        }));

        this.activeCounterTablesData.set(orderedList);
        this.activeCounterTablesLoading.set(false);
      },
      error: (error: any) => {
        console.error(error);
        this.activeCounterTablesLoading.set(false);
      },
    });
  }

  onChangeApplicationType() {
    this.getPortsActivity();
  }

  // Date Picker Operations
  onChangeSummaryDateRange(event: any) {
    this.selectedDateRange.set(event);

    if (this.getIsSameDay()) {
      this.selectedStartTime.set('00:00');
      this.previousStartTime.set('00:00');
      this.selectedEndTime.set('23:59');
      this.previousEndTime.set('23:59');
    }

    this.getPortsActivity();
  }

  onChangeSummaryStartTime(event: any) {
    const time = new Date(event).getHours() + ':' + new Date(event).getMinutes();
    this.selectedStartTime.set(time);
  }

  onCloseSummaryStartTime() {
    if (this.previousStartTime() !== this.selectedStartTime()) {
      this.previousStartTime.set(this.selectedStartTime());
      this.getPortsActivity();
    }
  }

  onChangeSummaryEndTime(event: any) {
    const time = new Date(event).getHours() + ':' + new Date(event).getMinutes();
    this.selectedEndTime.set(time);
  }

  onCloseSummaryEndTime() {
    if (this.previousEndTime() !== this.selectedEndTime()) {
      this.previousEndTime.set(this.selectedEndTime());
      this.getPortsActivity();
    }
  }

  cancelSummaryDateRange(event: any) {
    this.selectedDateRange.set([new Date(), new Date()]);
    this.getPortsActivity();
  }
}
